/** PRO-18 Mon compte (profil, mot de passe, MFA facultative, sessions) · PRO-21 Données personnelles (RGPD). */
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, Table, TextField, useToast } from '@myron/design-system';
import { Download, KeyRound, LogOut, ShieldCheck, ShieldOff } from 'lucide-react';
import type { S } from '@myron/contracts';
import { del, get, patch, post, put, ApiErreur } from '../../../shared/api/client';
import { useSession } from '../../../shared/auth/session';
import { Confirmation, EntetePage, ErreurFormulaire, EtatRequete, MentionSimule, StatutBadge } from '../../../shared/ui/composants';
import { dateHeure } from '../../../shared/ui/format';

type UtilisateurClient = S<'UtilisateurClient'>;
interface SessionResume { id: string; creeLe: string; derniereActiviteLe: string; appareil: string; courante: boolean }

export function PageMonCompte() {
  return (
    <>
      <EntetePage titre="Mon compte" sousTitre="Profil, mot de passe, vérification en deux étapes et sessions." fil={[{ libelle: 'Portail', vers: '/pro' }, { libelle: 'Mon compte' }]} />
      <div className="my-grille-2">
        <div className="my-pile my-pile--large">
          <BlocProfil />
          <BlocMotDePasse />
          <BlocSessions />
        </div>
        <div className="my-pile my-pile--large">
          <BlocMfa />
          <Card as="section" aria-labelledby="titre-rgpd-lien">
            <div className="my-pile my-pile--serre">
              <h2 id="titre-rgpd-lien" className="my-titre-4">Données personnelles</h2>
              <p className="my-petit my-secondaire">Exporter vos données ou demander l’effacement de votre compte.</p>
              <Link to="/pro/donnees-personnelles">Gérer mes données personnelles</Link>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}

function BlocProfil() {
  const qc = useQueryClient();
  const toast = useToast();
  const { rafraichir } = useSession();
  const q = useQuery({ queryKey: ['pro', 'compte'], queryFn: () => get<UtilisateurClient>('/pro/compte') });
  const [f, setF] = useState<{ prenom: string; nom: string; telephone: string } | null>(null);
  const m = useMutation({
    mutationFn: (v: { prenom: string; nom: string; telephone: string }) => patch<UtilisateurClient>('/pro/compte', { prenom: v.prenom, nom: v.nom, ...(v.telephone ? { telephone: v.telephone } : {}) }, { version: q.data!.version }),
    onSuccess: (u) => { qc.setQueryData(['pro', 'compte'], u); setF(null); void rafraichir(); toast({ ton: 'succes', titre: 'Profil enregistré' }); },
  });
  return (
    <Card as="section" aria-labelledby="titre-profil">
      <div className="my-pile">
        <h2 id="titre-profil" className="my-titre-4">Profil</h2>
        <EtatRequete requete={q} enfants={(u) => {
          const v = f ?? { prenom: u.prenom ?? '', nom: u.nom ?? '', telephone: '' };
          return (
            <form className="my-form" onSubmit={(e) => { e.preventDefault(); m.mutate(v); }}>
              <dl className="my-dl"><dt>E-mail</dt><dd>{u.email}</dd><dt>Rôle</dt><dd>{u.role === 'CLIENT_ADMIN' ? 'Administrateur entreprise' : u.role === 'CLIENT_ACHETEUR' ? 'Acheteur' : 'Comptabilité'}</dd></dl>
              <div className="my-form-ligne">
                <TextField label="Prénom" value={v.prenom} onChange={(e) => setF({ ...v, prenom: e.target.value })} required autoComplete="given-name" />
                <TextField label="Nom" value={v.nom} onChange={(e) => setF({ ...v, nom: e.target.value })} autoComplete="family-name" />
              </div>
              <TextField label="Téléphone (facultatif)" type="tel" value={v.telephone} onChange={(e) => setF({ ...v, telephone: e.target.value })} aide="Champ sensible : chiffré en production (SIMULÉ ici)." autoComplete="tel" />
              <ErreurFormulaire erreur={m.error} />
              <div><Button type="submit" chargement={m.isPending} disabled={!f}>Enregistrer</Button></div>
            </form>
          );
        }} />
      </div>
    </Card>
  );
}

function BlocMotDePasse() {
  const toast = useToast();
  const [v, setV] = useState({ actuel: '', nouveau: '', confirmation: '' });
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const m = useMutation({
    mutationFn: () => put('/auth/mot-de-passe', { motDePasseActuel: v.actuel, nouveauMotDePasse: v.nouveau }),
    onSuccess: () => { setV({ actuel: '', nouveau: '', confirmation: '' }); toast({ ton: 'succes', titre: 'Mot de passe modifié', texte: 'Vos autres sessions ont été fermées.' }); },
    onError: (e) => { if (e instanceof ApiErreur) setErreurs(e.champs); },
  });
  const valider = (e: FormEvent) => {
    e.preventDefault();
    const er: Record<string, string> = {};
    if (!v.actuel) er.motDePasseActuel = 'Requis';
    if (v.nouveau.length < 12) er.nouveauMotDePasse = '12 caractères minimum';
    if (v.confirmation !== v.nouveau) er.confirmation = 'Les mots de passe ne correspondent pas';
    setErreurs(er);
    if (!Object.keys(er).length) m.mutate();
  };
  return (
    <Card as="section" aria-labelledby="titre-mdp-compte">
      <form className="my-form" onSubmit={valider} noValidate>
        <h2 id="titre-mdp-compte" className="my-titre-4">Mot de passe</h2>
        <TextField label="Mot de passe actuel" type="password" autoComplete="current-password" value={v.actuel} onChange={(e) => setV({ ...v, actuel: e.target.value })} erreur={erreurs.motDePasseActuel} />
        <div className="my-form-ligne">
          <TextField label="Nouveau mot de passe" type="password" autoComplete="new-password" value={v.nouveau} onChange={(e) => setV({ ...v, nouveau: e.target.value })} erreur={erreurs.nouveauMotDePasse} />
          <TextField label="Confirmation" type="password" autoComplete="new-password" value={v.confirmation} onChange={(e) => setV({ ...v, confirmation: e.target.value })} erreur={erreurs.confirmation} />
        </div>
        {m.isError && !Object.keys(erreurs).length ? <ErreurFormulaire erreur={m.error} /> : null}
        <div><Button type="submit" icone={KeyRound} chargement={m.isPending}>Changer le mot de passe</Button></div>
      </form>
    </Card>
  );
}

function BlocMfa() {
  const { session, rafraichir } = useSession();
  const toast = useToast();
  const actif = !!session?.utilisateur.mfaActive;
  const [secret, setSecret] = useState<{ secretBase32: string } | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [confirmerDesactivation, setConfirmerDesactivation] = useState(false);
  const demarrer = useMutation({ mutationFn: () => post<{ secretBase32: string }>('/auth/mfa/enrolement'), onSuccess: setSecret });
  const confirmer = useMutation({
    mutationFn: () => post<{ codes: string[] }>('/auth/mfa/enrolement/confirmer', { code: code.trim() }),
    onSuccess: (r) => { setCodes(r.codes); setSecret(null); setCode(''); void rafraichir(); toast({ ton: 'succes', titre: 'Vérification en deux étapes activée' }); },
  });
  const regenerer = useMutation({ mutationFn: () => post<{ codes: string[] }>('/auth/mfa/codes-recuperation'), onSuccess: (r) => setCodes(r.codes) });
  const desactiver = useMutation({
    mutationFn: () => del('/auth/mfa'),
    onSuccess: () => { setConfirmerDesactivation(false); setCodes(null); void rafraichir(); toast({ ton: 'info', titre: 'Vérification en deux étapes désactivée' }); },
  });
  return (
    <Card as="section" aria-labelledby="titre-mfa-compte">
      <div className="my-pile">
        <div className="my-ligne my-ligne--espace">
          <h2 id="titre-mfa-compte" className="my-titre-4">Vérification en deux étapes</h2>
          {actif ? <Badge ton="succes">Activée</Badge> : <Badge>Désactivée</Badge>}
        </div>
        <p className="my-petit my-secondaire">Facultative pour les clients, recommandée. Elle protège vos prix et commandes même si votre mot de passe est compromis. <MentionSimule /></p>
        {!actif && !secret && <div><Button icone={ShieldCheck} chargement={demarrer.isPending} onClick={() => demarrer.mutate()}>Activer la vérification en deux étapes</Button></div>}
        {secret && (
          <form className="my-form" onSubmit={(e) => { e.preventDefault(); confirmer.mutate(); }}>
            <p>Ajoutez ce secret à votre application d’authentification :</p>
            <p><code className="my-code" data-testid="secret-mfa-compte">{secret.secretBase32}</code></p>
            <TextField label="Code à 6 chiffres" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} />
            <ErreurFormulaire erreur={confirmer.error} />
            <div className="my-ligne"><Button type="submit" chargement={confirmer.isPending} disabled={code.length !== 6}>Confirmer</Button><Button variante="tertiaire" onClick={() => setSecret(null)}>Annuler</Button></div>
          </form>
        )}
        {actif && (
          <div className="my-ligne">
            <Button variante="secondaire" chargement={regenerer.isPending} onClick={() => regenerer.mutate()}>Régénérer les codes de récupération</Button>
            <Button variante="danger" icone={ShieldOff} onClick={() => setConfirmerDesactivation(true)}>Désactiver</Button>
          </div>
        )}
        <ErreurFormulaire erreur={demarrer.error ?? regenerer.error} />
        {codes && (
          <Alert ton="succes" titre="Codes de récupération (affichés une seule fois)">
            <ul className="my-grille" style={{ gridTemplateColumns: 'repeat(2, minmax(0,1fr))', listStyle: 'none', padding: 0 }} data-testid="codes-recuperation-compte">
              {codes.map((c) => <li key={c}><code className="my-code">{c}</code></li>)}
            </ul>
          </Alert>
        )}
        <Confirmation ouverte={confirmerDesactivation} titre="Désactiver la vérification en deux étapes ?" texte="Vos autres sessions seront fermées et vos codes de récupération supprimés." libelleAction="Désactiver" danger onConfirmer={() => desactiver.mutate()} onFermer={() => setConfirmerDesactivation(false)} chargement={desactiver.isPending} erreur={desactiver.error} />
      </div>
    </Card>
  );
}

function BlocSessions() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['auth', 'sessions'], queryFn: () => get<SessionResume[]>('/auth/sessions') });
  const revoquer = useMutation({ mutationFn: (id: string) => del(`/auth/sessions/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ['auth', 'sessions'] }) });
  const autres = useMutation({ mutationFn: () => del('/auth/sessions'), onSuccess: () => qc.invalidateQueries({ queryKey: ['auth', 'sessions'] }) });
  return (
    <Card as="section" aria-labelledby="titre-sessions">
      <div className="my-pile">
        <div className="my-ligne my-ligne--espace">
          <h2 id="titre-sessions" className="my-titre-4">Sessions actives</h2>
          <Button variante="secondaire" compact icone={LogOut} chargement={autres.isPending} onClick={() => autres.mutate()} disabled={(q.data ?? []).length <= 1}>Fermer les autres sessions</Button>
        </div>
        <EtatRequete requete={q} enfants={(l) => (
          <Table<SessionResume>
            legende="Sessions actives"
            cle={(s) => s.id}
            lignes={l}
            colonnes={[
              { cle: 'app', titre: 'Appareil', rendu: (s) => <>{s.appareil} {s.courante && <Badge ton="marque">Session actuelle</Badge>}</> },
              { cle: 'act', titre: 'Dernière activité', rendu: (s) => dateHeure(s.derniereActiviteLe) },
              { cle: 'act2', titre: 'Actions', actions: true, rendu: (s) => (s.courante ? null : <Button compact variante="tertiaire" onClick={() => revoquer.mutate(s.id)} aria-label={`Fermer la session ${s.appareil} ouverte le ${dateHeure(s.creeLe)}`}>Fermer</Button>) },
            ]}
          />
        )} />
        <ErreurFormulaire erreur={revoquer.error ?? autres.error} />
      </div>
    </Card>
  );
}

// ───────────────────────────────── Données personnelles ─────────────────────────────────
interface DemandeRgpd { id: string; type: 'ACCES' | 'EFFACEMENT' | 'RECTIFICATION'; statut: string; recueLe: string; traiteeLe?: string; export?: unknown; precision?: string }

function lienExport(d: DemandeRgpd) {
  return URL.createObjectURL(new Blob([JSON.stringify(d.export, null, 2)], { type: 'application/json' }));
}

export function PageDonneesPersonnelles() {
  const qc = useQueryClient();
  const toast = useToast();
  const [effacement, setEffacement] = useState(false);
  const [dernierExport, setDernierExport] = useState<DemandeRgpd | null>(null);
  const q = useQuery({ queryKey: ['pro', 'rgpd'], queryFn: () => get<DemandeRgpd[]>('/pro/rgpd/demandes') });
  const creer = useMutation({
    mutationFn: (v: { type: DemandeRgpd['type']; precision?: string }) => post<DemandeRgpd>('/pro/rgpd/demandes', v),
    onSuccess: (d) => {
      void qc.invalidateQueries({ queryKey: ['pro', 'rgpd'] });
      setEffacement(false);
      if (d.type === 'ACCES') setDernierExport(d);
      else toast({ ton: 'info', titre: 'Demande enregistrée', texte: 'Elle sera traitée par notre délégué à la protection des données.' });
    },
  });
  return (
    <>
      <EntetePage titre="Mes données personnelles" sousTitre="Exercez vos droits d’accès, de rectification et d’effacement (RGPD)." fil={[{ libelle: 'Portail', vers: '/pro' }, { libelle: 'Données personnelles' }]} />
      <div className="my-pile my-pile--large">
        <Card as="section" aria-labelledby="titre-droits">
          <div className="my-pile">
            <h2 id="titre-droits" className="my-titre-4">Vos droits</h2>
            <p className="my-secondaire">L’export contient uniquement vos données (profil, sessions, commandes que vous avez passées). L’effacement anonymise votre compte : les commandes et factures sont conservées de façon pseudonymisée au titre des obligations comptables.</p>
            <div className="my-ligne">
              <Button icone={Download} chargement={creer.isPending && creer.variables?.type === 'ACCES'} onClick={() => creer.mutate({ type: 'ACCES' })}>Exporter mes données</Button>
              <Button variante="danger" onClick={() => setEffacement(true)}>Demander l’effacement de mon compte</Button>
            </div>
            {dernierExport && (
              <Alert ton="succes" titre="Export prêt">
                <a href={lienExport(dernierExport)} download="myron-mes-donnees.json">Télécharger mes données (JSON)</a>
              </Alert>
            )}
            <ErreurFormulaire erreur={creer.error} />
          </div>
        </Card>
        <Card as="section" aria-labelledby="titre-demandes">
          <div className="my-pile">
            <h2 id="titre-demandes" className="my-titre-4">Historique de mes demandes</h2>
            <EtatRequete requete={q} vide={(l) => !l.length} titreVide="Aucune demande" enfants={(l) => (
              <Table<DemandeRgpd>
                legende="Mes demandes RGPD"
                cle={(d) => d.id}
                lignes={l}
                colonnes={[
                  { cle: 't', titre: 'Type', rendu: (d) => ({ ACCES: 'Accès (export)', EFFACEMENT: 'Effacement', RECTIFICATION: 'Rectification' })[d.type] },
                  { cle: 'r', titre: 'Reçue le', rendu: (d) => dateHeure(d.recueLe) },
                  { cle: 's', titre: 'Statut', rendu: (d) => <StatutBadge statut={d.statut === 'RECUE' ? 'EN_ATTENTE' : d.statut === 'TRAITEE' ? 'TERMINEE' : d.statut} /> },
                  { cle: 'a', titre: 'Export', actions: true, rendu: (d) => (d.export ? <a href={lienExport(d)} download={`myron-donnees-${d.id.slice(0, 8)}.json`}>Télécharger</a> : '—') },
                ]}
              />
            )} />
          </div>
        </Card>
      </div>
      <Confirmation
        ouverte={effacement}
        titre="Demander l’effacement de votre compte ?"
        texte="Après traitement, vous ne pourrez plus vous connecter. Les commandes restent conservées de façon pseudonymisée."
        libelleAction="Envoyer la demande"
        danger
        motif="optionnel"
        onConfirmer={(m) => creer.mutate({ type: 'EFFACEMENT', ...(m ? { precision: m } : {}) })}
        onFermer={() => setEffacement(false)}
        chargement={creer.isPending}
        erreur={creer.error}
      />
    </>
  );
}

